import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ProcessingScreen } from ".";
import { Recording } from "../types";
import { apiFetch } from "../utils/api";
import {
  buildRetainedTranscriptReview,
  buildWorkflowTranscriptReview,
  TranscriptReview,
  transcriptReviewHasText,
} from "../utils/transcriptReview";
import AnonymizedTextDisplay from "./AnonymizedTextDisplay";
import RecordingAudioPlayback, {
  RecordingRetranscriptionAction,
} from "./RecordingAudioPlayback";
import ReactMarkdown from "react-markdown";
import { AlertTriangle, Eye, EyeOff, Wrench } from "lucide-react";
import {
  recoveryActionButtonClassName,
  recoveryActionExplainerClassName,
  recoveryActionRowClassName,
} from "./recoveryActionLayout";

const disclosureToggleClassName =
  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-brand-blauw transition-colors hover:bg-brand-primary/40";

interface TranscriptStatusChange {
  isProcessing: boolean;
  hasText: boolean;
  hasError?: boolean;
}

interface TaskDisplayProps {
  workflowId: string;
  type?: string;
  onLoaded: () => void;
  onStatusChange?: (status: TranscriptStatusChange) => void;
  lengthOfRecording?: number | undefined;
  conversationId?: string;
  recording?: Recording | null;
  onReplaceRecording?: () => void;
  onIgnoreFailedRecording?: () => void;
  onRetranscriptionStarted?: (workflowId: string) => void;
  recoveryControlsEnabled?: boolean;
}

const TaskDisplay: React.FC<TaskDisplayProps> = ({
  workflowId,
  type = "Transcription",
  onLoaded,
  onStatusChange,
  lengthOfRecording,
  conversationId,
  recording,
  onReplaceRecording,
  onIgnoreFailedRecording,
  onRetranscriptionStarted,
  recoveryControlsEnabled = true,
}) => {
  const retainedReview = useCallback(
    () => buildRetainedTranscriptReview(recording, workflowId),
    [recording, workflowId],
  );

  const [transcriptReview, setTranscriptReview] =
    useState<TranscriptReview | null>(() => retainedReview());
  const [isProcessing, setIsProcessing] = useState(
    !retainedReview() && Boolean(workflowId),
  );
  const [showTranscript, setShowTranscript] = useState(false);
  const [showDangerousActions, setShowDangerousActions] = useState(false);
  const canReplaceRecording = Boolean(
    recoveryControlsEnabled && recording?.is_active && onReplaceRecording,
  );
  const canRetranscribeRecording = Boolean(
    recoveryControlsEnabled &&
    conversationId &&
    recording?.is_active &&
    recording.has_raw_audio &&
    recording.can_retranscribe,
  );
  const canShowDangerousActions =
    canReplaceRecording || canRetranscribeRecording;
  const canIgnoreFailedRecording = Boolean(
    recoveryControlsEnabled &&
    recording?.recording_type === "voice-over" &&
    recording?.is_active &&
    onIgnoreFailedRecording,
  );

  const handleIgnoreFailedRecording = async () => {
    if (
      !conversationId ||
      !canIgnoreFailedRecording ||
      !onIgnoreFailedRecording
    )
      return;

    const response = await apiFetch(
      `/conversation/${conversationId}/recordings/voice-over/ignore-failed`,
      { method: "POST" },
    );
    if (!response.ok) {
      throw new Error(`Ignore failed voice-over failed: ${response.status}`);
    }
    onIgnoreFailedRecording();
  };

  const handleReplaceRecording = () => {
    if (!canReplaceRecording || !onReplaceRecording) return;

    const confirmed = window.confirm(
      type === "Gesprek"
        ? "Nieuwe opname maken? De bestaande gespreksopname, persoonlijke opmerkingen en analyse worden direct vervangen."
        : "Nieuwe opname maken? De bestaande persoonlijke opmerkingen en analyse worden direct vervangen.",
    );
    if (confirmed) {
      onReplaceRecording();
    }
  };

  const pollTaskStatus = useCallback(
    (workflow_id: string) => {
      let pollId: ReturnType<typeof setInterval> | null = null;
      onStatusChange?.({ isProcessing: true, hasText: false });

      const fetchStatus = async () => {
        try {
          const statusResponse = await apiFetch(
            `/audio/transcribe/result/${workflow_id}`,
          );
          if (!statusResponse.ok) {
            const errorMessage = `HTTP ${statusResponse.status}: ${statusResponse.statusText}.`;
            toast.error("Verbindingsfout", {
              description: `Kon geen status ophalen: ${errorMessage}`,
              duration: 5000,
            });
            throw new Error(`Failed to fetch task status: ${errorMessage}`);
          }

          const res = await statusResponse.json();

          if (
            res.text ||
            (res.error_message &&
              res.error_message !== "Workflow not completed")
          ) {
            const review = buildWorkflowTranscriptReview(res, workflow_id);
            const hasText = transcriptReviewHasText(review);
            const hasError = Boolean(
              review.errorMessage &&
              review.errorMessage !== "Workflow not completed",
            );

            setTranscriptReview(review);

            if (pollId) {
              clearInterval(pollId);
              pollId = null;
            }
            setIsProcessing(false);

            if (hasError && review.errorMessage) {
              toast.error("Verwerking mislukt", {
                description: review.errorMessage,
                duration: 8000,
                action: {
                  label: "Opnieuw proberen",
                  onClick: () => {
                    setIsProcessing(true);
                    pollTaskStatus(workflow_id);
                  },
                },
              });
            }

            onStatusChange?.({ isProcessing: false, hasText, hasError });
            onLoaded();
          }
        } catch (error) {
          console.error("Error polling task status:", error);
          if (pollId) {
            clearInterval(pollId);
            pollId = null;
          }

          const errorMessage =
            error instanceof Error
              ? error.message
              : "Onbekende fout opgetreden";
          setTranscriptReview({
            workflowId,
            redactedTranscript: "",
            executiveSummary: null,
            errorMessage,
          });
          setIsProcessing(false);
          onStatusChange?.({
            isProcessing: false,
            hasText: false,
            hasError: true,
          });

          toast.error("Verbindingsfout", {
            description:
              "Kon de taakstatus niet ophalen. Controleer je internetverbinding.",
            duration: 8000,
            action: {
              label: "Opnieuw proberen",
              onClick: () => {
                setIsProcessing(true);
                pollTaskStatus(workflow_id);
              },
            },
          });
        }
      };

      fetchStatus();
      pollId = setInterval(fetchStatus, 10000);

      return () => {
        if (pollId) clearInterval(pollId);
      };
    },
    [workflowId, onLoaded, onStatusChange],
  );

  useEffect(() => {
    const review = retainedReview();

    if (review) {
      setTranscriptReview(review);
      setIsProcessing(false);
      onStatusChange?.({
        isProcessing: false,
        hasText: transcriptReviewHasText(review),
      });
      onLoaded();
      return;
    }

    if (workflowId) {
      setIsProcessing(true);
      pollTaskStatus(workflowId);
    } else {
      setIsProcessing(false);
    }
  }, [
    workflowId,
    type,
    pollTaskStatus,
    retainedReview,
    onLoaded,
    onStatusChange,
  ]);

  const handleRetranscriptionStarted = useCallback(
    (newWorkflowId: string) => {
      setTranscriptReview(null);
      setIsProcessing(true);
      onRetranscriptionStarted?.(newWorkflowId);
      pollTaskStatus(newWorkflowId);
    },
    [onRetranscriptionStarted, pollTaskStatus],
  );

  const renderAudioPlayback = () =>
    recoveryControlsEnabled && conversationId && recording ? (
      <RecordingAudioPlayback
        conversationId={conversationId}
        recording={recording}
        label={type}
      />
    ) : null;

  // "Herstelopties" and "Toon transcript" are the same kind of control — a
  // quiet disclosure toggle — so they share one right-aligned row and one
  // style, instead of the recovery options sitting in a full-width box.
  const renderDisclosureToggles = () => {
    const hasTranscript = Boolean(transcriptReview?.redactedTranscript);

    if (!canShowDangerousActions && !hasTranscript) {
      return null;
    }

    return (
      <div className="my-4 flex flex-wrap items-center justify-end gap-1">
        {canShowDangerousActions && (
          <button
            type="button"
            onClick={() => setShowDangerousActions((isOpen) => !isOpen)}
            aria-expanded={showDangerousActions}
            className={disclosureToggleClassName}
          >
            <Wrench className="h-4 w-4" aria-hidden="true" />
            {showDangerousActions ? "Verberg herstelopties" : "Herstelopties"}
          </button>
        )}
        {hasTranscript && (
          <button
            type="button"
            onClick={() => setShowTranscript(!showTranscript)}
            aria-expanded={showTranscript}
            className={disclosureToggleClassName}
          >
            {showTranscript ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
            {showTranscript ? "Verberg transcript" : "Toon transcript"}
          </button>
        )}
      </div>
    );
  };

  // Rendered on both the success and the failure branch: a failed transcription
  // is exactly when the user needs "Nieuwe opname", so the options must not
  // live in the success branch only.
  const renderRecoveryOptions = () =>
    canShowDangerousActions && showDangerousActions ? (
      <div className="mb-4 rounded-lg border border-[#E1D6C6] bg-brand-primary/40 p-4">
        <p className="text-sm font-semibold text-brand-red">Herstelopties</p>
        <p className="mt-1 text-sm text-brand-blauw">
          Deze acties overschrijven bestaande resultaten.
        </p>
        <div className="mt-4 space-y-3">
          {canRetranscribeRecording && conversationId && (
            <RecordingRetranscriptionAction
              conversationId={conversationId}
              recording={recording}
              onRetranscriptionStarted={handleRetranscriptionStarted}
            />
          )}
          {canReplaceRecording && (
            <div className={recoveryActionRowClassName}>
              <button
                type="button"
                onClick={handleReplaceRecording}
                className={recoveryActionButtonClassName}
              >
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                Nieuwe opname
              </button>
              <p className={recoveryActionExplainerClassName}>
                Vervangt deze opname en wist de bijbehorende analyse zodra je
                bevestigt.
              </p>
            </div>
          )}
        </div>
      </div>
    ) : null;

  if (isProcessing) {
    return (
      <div className="bg-white border border-[#E1D6C6] rounded-xl p-8 shadow-sm">
        {renderAudioPlayback()}
        <ProcessingScreen
          isProcessing={isProcessing}
          taskId={workflowId}
          type={type}
          taskStatus={null}
          lengthOfRecording={lengthOfRecording}
        />
      </div>
    );
  }

  if (
    transcriptReview?.errorMessage &&
    transcriptReview.errorMessage !== "Workflow not completed"
  ) {
    return (
      <div className="bg-white border border-[#E1D6C6] rounded-xl p-8 shadow-sm">
        <div className="text-left mb-6">
          <h2 className="text-2xl font-semibold text-brand-red mb-2">{type}</h2>
          <p className="text-red-600 text-sm">
            ❌ Je {type.toLowerCase()} verwerking is mislukt.
          </p>
        </div>
        {renderAudioPlayback()}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">
            {transcriptReview.errorMessage}
          </p>
        </div>
        {renderDisclosureToggles()}
        {renderRecoveryOptions()}
        {canIgnoreFailedRecording && (
          <button
            type="button"
            onClick={handleIgnoreFailedRecording}
            className="mt-4 text-sm text-brand-blauw hover:underline"
          >
            Doorgaan zonder persoonlijke opmerkingen
          </button>
        )}
      </div>
    );
  }

  if (
    transcriptReview?.redactedTranscript ||
    transcriptReview?.executiveSummary
  ) {
    return (
      <div className="bg-white border border-[#E1D6C6] rounded-xl p-8 shadow-sm">
        <div className="text-left mb-6">
          <h2 className="text-2xl font-semibold text-brand-red mb-2">{type}</h2>
          <p className="text-brand-blauw text-base mb-2 font-semibold">
            Je {type.toLowerCase()} is succesvol verwerkt.
          </p>
          <div className="text-brand-black text-sm font-normal prose prose-sm max-w-none leading-tight [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-2 [&_h1]:mb-1 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-1 [&_h3]:mb-1 [&_p]:leading-tight [&_p]:my-1 [&_ul]:leading-tight [&_ol]:leading-tight [&_li]:leading-tight [&_pre]:border [&_pre]:border-[#E1D6C6] [&_pre]:bg-brand-primary/40 [&_pre]:text-brand-black [&_pre]:whitespace-pre-wrap [&_code]:bg-brand-primary/50 [&_code]:text-brand-black [&_code]:font-normal">
            <ReactMarkdown
              components={{
                h1: ({ node, children, ...props }) => (
                  <h3 {...props}>{children}</h3>
                ),
                h2: ({ node, children, ...props }) => (
                  <h4 {...props}>{children}</h4>
                ),
                h3: ({ node, children, ...props }) => (
                  <h5 {...props}>{children}</h5>
                ),
                h4: ({ node, children, ...props }) => (
                  <h6 {...props}>{children}</h6>
                ),
              }}
            >
              {transcriptReview.executiveSummary || ""}
            </ReactMarkdown>
          </div>
        </div>

        {renderAudioPlayback()}

        {renderDisclosureToggles()}

        {renderRecoveryOptions()}

        {showTranscript && transcriptReview.redactedTranscript && (
          <div className="bg-white border border-[#E1D6C6] rounded-xl p-6 max-h-96 overflow-y-auto">
            <AnonymizedTextDisplay
              anonymizedText={transcriptReview.redactedTranscript}
              originalText={transcriptReview.redactedTranscript}
              entities={transcriptReview.anonymizationResult?.entities}
              className="text-gray-700 text-sm leading-relaxed"
            />
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default TaskDisplay;
